import { useLayoutEffect, useRef } from "preact/hooks";
import { playbackSeconds, script, timeline } from "./state";
import { cueAt } from "./script";

export function Teleprompter({
  running,
  active,
}: {
  running: boolean;
  active: boolean;
}) {
  const viewport = useRef<HTMLDivElement>(null);
  const track = useRef<HTMLParagraphElement>(null);
  const words = script.value.words;
  const cues = timeline.value;
  const scrollTime = useRef({ words, seconds: 0 });

  useLayoutEffect(function () {
    const element = track.current;
    const container = viewport.current;
    if (!element || !container) return;
    const spans = Array.from(
      element.querySelectorAll<HTMLSpanElement>(".caption-word"),
    );
    let positions: number[] = [];
    let readingOffset = 0;
    let highlighted = -1;
    if (scrollTime.current.words !== words) {
      scrollTime.current = { words, seconds: 0 };
    }

    function update(seconds: number) {
      if (!element || !positions.length) return;
      if (running) scrollTime.current.seconds = seconds;
      else seconds = scrollTime.current.seconds;
      const current = running ? cueAt(cues, seconds) : -1;
      if (current !== highlighted) {
        spans[highlighted]?.classList.remove("current");
        spans[current]?.classList.add("current");
        highlighted = current;
      }
      let offset = 0;
      if (running || seconds > 0) {
        const index = current < 0 ? cues.length - 1 : current;
        const cue = cues[index];
        if (!cue) return;
        // Stop moving during an annotated pause, then resume with the next word.
        const spokenSeconds = 60 / cue.word.pace;
        const wordProgress = (seconds - cue.start) / spokenSeconds;
        const progress = Math.min(1, Math.max(0, wordProgress));
        const start = positions[index];
        const end = positions[index + 1];
        offset = start + (end - start) * progress;
      }
      const translation = readingOffset - offset;
      element.style.transform = `translate3d(0, ${translation}px, 0)`;
    }

    function measure() {
      if (!element || !container || !spans.length || !element.clientWidth) return;
      const lineHeight = parseFloat(getComputedStyle(element).lineHeight);
      const firstTop = spans[0].offsetTop;
      const firstWordCenter = firstTop + spans[0].offsetHeight / 2;
      // A line travels across a one-line band centered in the reading viewport.
      const readingCenter = (container.clientHeight + lineHeight) / 2;
      readingOffset = readingCenter - firstWordCenter;
      const rows: { start: number; top: number }[] = [];
      for (let index = 0; index < spans.length; index++) {
        const top = spans[index].offsetTop - firstTop;
        const previous = rows.at(-1);
        // Emphasized words have different font sizes but share the same line.
        if (!previous || top - previous.top > lineHeight / 2) {
          rows.push({ start: index, top });
        }
      }
      positions = [];
      for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
        const row = rows[rowIndex];
        const next = rows[rowIndex + 1];
        const end = next?.start ?? spans.length;
        const nextTop = next?.top ?? row.top + lineHeight;
        const count = end - row.start;
        for (let index = row.start; index < end; index++) {
          const progress = (index - row.start) / count;
          positions[index] = row.top + (nextTop - row.top) * progress;
        }
        positions[end] = nextTop;
      }
      update(playbackSeconds.peek());
    }

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    observer.observe(container);
    const unsubscribe = playbackSeconds.subscribe(update);
    return function () {
      observer.disconnect();
      unsubscribe();
      spans[highlighted]?.classList.remove("current");
    };
  }, [words, cues, running, active]);

  return (
    <div ref={viewport} class="caption" role="region" aria-label="Teleprompter">
      <p ref={track}>
        {words.map(function (word, index) {
          return (
            <span key={index} class={`caption-word intensity-${word.intensity}`}>
              {word.text.trim()}{" "}
            </span>
          );
        })}
      </p>
    </div>
  );
}
